import { cellBoxes, type CellBox } from "./frame-depth.ts";
import { footprintBlocks, minCoverForCell, type PropFootprint } from "./footprint.ts";

/**
 * A frame's saved depth in the shape ADR-131 gave it (`walkOn` and `walkUnder` rectangles of collision cells), read
 * where a stored override is read and converted to Behind and In front cells (ADR-132 decision 8). `FrameDepth` is
 * saved data, read and checked wherever an override is. Everything here is plain data in whole numbers and runs
 * unchanged in a browser, on a server and in a build.
 */

/**
 * A collision cell: its column and its row counted up from the frame's bottom, as a footprint holds it.
 */
export type Cell = readonly [column: number, rowUp: number];

/**
 * What a builder adds to a frame's footprint (ADR-131 decision 5). `walkOn` is ground a character stands on: it
 * never blocks, always draws under characters and counts as ground for the pixels above it. `walkUnder` is an
 * overhang whose pixels skip any ground in their column and sort with the whole frame. Both are rectangles of
 * cells counted from the frame's bottom-left. `cell` is the side in pixels of those cells (ADR-113 decision 9), absent
 * meaning the authoring cell. Nothing derived is stored.
 */
export interface FrameDepth {
  readonly walkOn?: readonly CellBox[];
  readonly walkUnder?: readonly CellBox[];
  readonly cell?: number;
}

/**
 * The frame a depth is derived for: its size in pixels, its base row (the row below its last solid pixel) and the
 * pixels of one collision cell.
 */
export interface GroundDepthFrame {
  readonly w: number;
  readonly h: number;
  readonly base: number;
  readonly cellPixels: number;
}

/**
 * The cells a frame's footprint blocks, the ground its pixels stand on (ADR-131 decision 1): the footprint after the
 * overrides, read as the panel draws it and the stamp lays it (`footprintBlocks`), so a footprint drawn as cells
 * gives those cells back and a derived one gives the cells its runs cover.
 *
 * @param footprint The frame's footprint, or undefined when it has none.
 * @param cellPixels Pixels per collision cell.
 * @returns The blocked cells, `[column, rowUp]`; none for a frame with no footprint.
 */
export function blockedCellsOf(footprint: PropFootprint | undefined, cellPixels: number): Cell[] {
  return footprint === undefined ? [] : footprintBlocks(footprint, cellPixels, false, minCoverForCell(cellPixels));
}

/**
 * Whether a depth holds a rectangle of either kind.
 *
 * @param depth The depth.
 * @returns True when it paints a cell.
 */
export function frameDepthPaints(depth: FrameDepth): boolean {
  return (depth.walkOn ?? []).length > 0 || (depth.walkUnder ?? []).length > 0;
}

/**
 * A painting as the depth that is saved: each kind's cells as rectangles (`cellBoxes`), a kind with no cell left
 * out, so equal paintings are equal bytes however they were painted.
 *
 * @param painted The Walk on and Walk under cells, `[column, rowUp]`.
 * @returns The depth, which says nothing (`isEmptyFrameDepth`) when nothing is painted.
 * @throws RangeError when a cell's coordinate is out of range (`cellBoxes`).
 */
export function frameDepthOfCells(painted: { readonly walkOn: readonly Cell[]; readonly walkUnder: readonly Cell[] }): FrameDepth {
  const walkOn = cellBoxes(painted.walkOn);
  const walkUnder = cellBoxes(painted.walkUnder);
  return { ...(walkOn.length === 0 ? {} : { walkOn }), ...(walkUnder.length === 0 ? {} : { walkUnder }) };
}

/**
 * Whether a depth says nothing: it holds no rectangle. Such a depth is the same as none.
 *
 * @param depth The depth.
 * @returns True when nothing is painted.
 */
export function isEmptyFrameDepth(depth: FrameDepth): boolean {
  return !frameDepthPaints(depth);
}
